import { useCallback, useContext, useEffect, useState } from 'react';

import { AppContext } from '@/app.context';
import { Logger } from '@shared/lib/logger';

import { measureStorage, type StorageBreakdown } from './measure-storage';

const logger = new Logger('storage-breakdown');

/**
 * What the app stores on this device, by group (`measureStorage`). Measured
 * on mount and again whenever `revision` changes (e.g. the list of
 * downloaded issues), or on `refresh()`. `breakdown` is `undefined` while
 * the first measurement runs, and stays so if it fails (`failed`).
 */
export function useStorageBreakdown(revision?: string): {
	breakdown: StorageBreakdown | undefined;
	failed: boolean;
	refresh: () => void;
} {
	const { db } = useContext(AppContext);
	const [breakdown, setBreakdown] = useState<StorageBreakdown>();
	const [failed, setFailed] = useState(false);
	const [tick, setTick] = useState(0);

	useEffect(() => {
		let cancelled = false;
		measureStorage(db).then(
			(result) => {
				if (!cancelled) {
					setBreakdown(result);
					setFailed(false);
				}
			},
			(error: unknown) => {
				logger.error('Failed to measure storage', error);
				if (!cancelled) {
					setFailed(true);
				}
			},
		);
		return () => {
			cancelled = true;
		};
	}, [db, revision, tick]);

	const refresh = useCallback(() => setTick((value) => value + 1), []);
	return { breakdown, failed, refresh };
}
